import { object, string, number, array, ZodIssueCode, preprocess } from 'zod';
import type { RefinementCtx } from 'zod';

import { makeZodValidate } from '@occc-app/design-system';
import {
  IS_REQUIRED,
  IS_LATITUDE_VALID_RANGE,
  IS_LONGITUDE_VALID_RANGE,
  IS_ZIP_CODE,
} from '@occc-app/lib/constants/validationMessages';

const optionalGeoCoord = (rangeError: string, min: number, max: number) =>
  preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    number().min(min, rangeError).max(max, rangeError).optional(),
  );

export const geoLocationSchema = object({
  latitude: optionalGeoCoord(IS_LATITUDE_VALID_RANGE, -90, 90),
  longitude: optionalGeoCoord(IS_LONGITUDE_VALID_RANGE, -180, 180),
}).superRefine((data, ctx: RefinementCtx) => {
  if (data.longitude !== undefined && data.latitude === undefined) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['latitude'],
      message: 'Longitude cannot be submitted without latitude.',
    });
  }
  if (data.latitude !== undefined && data.longitude === undefined) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['longitude'],
      message: 'Latitude cannot be submitted without longitude.',
    });
  }
});

const locationConfigurationSchema = object({
  address: object({
    street1: string({ required_error: IS_REQUIRED }).min(1, IS_REQUIRED),
    city: string({ required_error: IS_REQUIRED }).min(1, IS_REQUIRED),
    state: object({}, { required_error: IS_REQUIRED }),
    countryCode: string({ required_error: IS_REQUIRED }).min(1, IS_REQUIRED),
    zipCode: string({ required_error: IS_REQUIRED }).regex(/^\d{5}(-?\d)*$/, IS_ZIP_CODE),
    geoLocation: geoLocationSchema.nullable().optional(),
  }),
  altriaRcn: string().nullable().optional(),
  altriaMan: string().nullable().optional(),
  rjrAccountNumber: string().nullable().optional(),
  rjrRbdsCode: string().nullable().optional(),
  timezone: object({}, { required_error: IS_REQUIRED }),
  locationGroups: array(
    object({
      uuid: string({ required_error: IS_REQUIRED }).min(1, IS_REQUIRED),
      name: string({ required_error: IS_REQUIRED }).min(1, IS_REQUIRED),
    }),
  )
    .nullable()
    .optional(),
});

export const validateLocationForm = makeZodValidate(locationConfigurationSchema);
